import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { EditAccountWebComponent } from '../component/EditAccountWebComponent';
import { EditAccountWebView } from '../view/EditAccountWebView';
import { templateEditAccount } from '../view/template';
import { EditAccountEvents, EditedAccount } from '../types';
import { showToast } from '../../helpers/showToast';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { translateFn } from '../../../utils/translator.helper';
import { AccountLoginSecurityEvents } from '../../common/accountLoginSecurity/types';
import { isHidden, toggleVisibility } from '../../../utils/htmlElement.utils';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { SelectedTargets, TargetsViewData } from '../../../targets/type';
import _ from 'lodash';
import { UserFormSelectedCompanyDataProvider } from '../../dataProvider/UserFormSelectedCompanyDataProvider';
import { combineLatest, Subscription } from 'rxjs';
import { AccountEvents } from '../../common/types/AccountsEvents';
import { renderSelectTargetsComponent } from '../../helpers/renderSelectTargetsComponent';
import { AccountSource } from '../../common/types/AccountSources';
import { AccountDataProvider } from '../../dataProvider/AccountDataProvider';

export class EditAccountWebController extends BaseWebComponentController {
  public component: EditAccountWebComponent;
  public view: EditAccountWebView;
  public viewConstructor: typeof EditAccountWebView;

  private userFormSelectedCompanyDataProvider: UserFormSelectedCompanyDataProvider;
  private originalAccountData: EditedAccount;
  private formSubscription: Subscription;
  private isFormValid: boolean;

  constructor(component: EditAccountWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: EditAccountWebView,
      viewTemplate: templateEditAccount,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$loginSecurity,
        event: AccountLoginSecurityEvents.AUTHENTICATION_METHOD_CHANGED,
        callback: this.onAuthenticationMethodChange,
      },
      {
        element: this.view.dom.$roleAndPrivileges,
        event: AccountEvents.ToggleSelectTargets,
        callback: this.toggleSelectTargets,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeEditAccountForm,
      },
      {
        element: this.view.dom.$updateButton,
        event: 'click',
        callback: this.onUpdateAccountClick,
      },
      {
        element: this.view.dom.$wrapper,
        event: EditAccountEvents.InitEditAccountForm,
        callback: this.setFormValidation,
      },
    ];
  }

  private closeEditAccountForm(): void {
    this.component.dispatchEvent(
      new CustomEvent(EditAccountEvents.Cancel, {
        bubbles: true,
      })
    );
  }

  private checkIfRightsHasBeenChanged(): boolean {
    const newRights = this.view.dom.$roleAndPrivileges.getSelectedRights();
    const oldRoleRights = _.get(this.originalAccountData, ['role', 'rights'], false);

    for (const right in oldRoleRights) {
      if (oldRoleRights[right] !== newRights[right]) {
        return true;
      }
    }
    return false;
  }

  private async onUpdateAccountClick(): Promise<void> {
    const securityGroupRule = _.get(this.originalAccountData, ['securityGroupRule', 'name'], false);
    if (!this.dependencies.isCloud() && securityGroupRule && this.checkIfRightsHasBeenChanged()) {
      this.showUnlinkConfirmationWindow();
      return;
    }

    await this.submitUpdateAccountForm();
  }

  private async submitUpdateAccountForm(): Promise<void> {
    try {
      if (!this.isFormValid) {
        this.setFormValidation();
        return;
      }

      await this.updateAccountRecord();

      showToast(ToastSeverity.SUCCESS, translateFn('Account updated successfully.'));
      this.component.dispatchEvent(
        new CustomEvent(EditAccountEvents.AccountUpdated, {
          bubbles: true,
        })
      );
    } catch (error) {
      showToast(ToastSeverity.ERROR, error.err.message);
    }
  }

  private showUnlinkConfirmationWindow(): void {
    const translations = this.view.getTranslations();
    const windowBodyHtml = `
      <p>
        ${translations.unlinkConfirmationString}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translations.unlinkConfirmationButtonString}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translations.cancelButtonLabel}
      </gz-button>`;

    const windowTitle = translations.unlinkConfirmationWindowTitle;
    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        await this.submitUpdateAccountForm();
      },
      null,
      true
    );
  }

  private async updateAccountRecord(): Promise<void> {
    this.view.dom.$updateButton.setLoadingState(true);

    try {
      const record = {
        _id: this.view.dom.$details.getUserIdValue(),
        userName: !this.dependencies.isCloud() ? this.view.dom.$details.getUsernameValue() : '',
        email: this.view.dom.$details.getEmailValue(),
        created: null,
        twoFactorAuthentication: this.component.getViewData().twoFactorAuthentication,
        password: this.view.dom.$details.getPasswordValue(),
        profile: {
          fullName: this.view.dom.$details.getFullNameValue(),
          timezone: this.view.dom.$details.getSelectedTimezone(),
          language: this.view.dom.$details.getSelectedLanguage(),
        },
        phoneNumber: {
          countryCode: this.view.dom.$details.getSelectedCountryCode(),
          subscriberNumber: this.view.dom.$details.getPhoneNumberValue(),
        },
        concurrentSessions: {
          enabled: this.view.dom.$loginSecurity.getConcurrentSessions()
        },
        source: this.view.accountSource,
        company: {
          id: this.view.currentCompany._id,
        },
        role: {
          roleId: this.view.dom.$roleAndPrivileges.getSelectedRoleId(),
          rights: this.view.dom.$roleAndPrivileges.getSelectedRights(),
        },
      };

      if (this.view.accountSource === AccountSource.MANUAL && this.originalAccountData.source === AccountSource.VIFOR) {
        record.source = AccountSource.VIFOR;
      }

      if (
        ([AccountSource.VIFOR, AccountSource.MANUAL, AccountSource.NEXUS].includes(this.view.accountSource)) &&
        this.dependencies.isCloud()
      ) {
        record['accountLockdown'] = {
          enabled: this.view.dom.$loginSecurity.getAccountLockDown(),
        };
        record['passwordLifetime'] = this.view.dom.$loginSecurity.getPasswordLifetime();
      }

      record['serviceTargets'] = [] as SelectedTargets;
      if (!isHidden(this.view.dom.$targetsSelection)) {
        record['serviceTargets'] = this.view.dom.$targetsSelection.getSelectedTargets();
      }

      const dataProvider = new AccountDataProvider();
      await dataProvider.updateRecords([record]);
    } catch (error: any) {
      showToast(ToastSeverity.ERROR, error?.err?.message);
    } finally {
      this.view.dom.$updateButton.setLoadingState(false);
    }
  }

  private async toggleSelectTargets(event: WebComponentEvent): Promise<void> {
    // remove select targets if this should not be visible
    if (!event.detail.targetsShouldBeVisible) {
      toggleVisibility(this.view.dom.$targetsSelection, false);
      this.setFormValidation();
      return;
    }

    if (event.detail.targetsShouldBeVisible) {
      toggleVisibility(this.view.dom.$targetsSelection, true);

      const showOnlyCompanies = this.view.dom.$roleAndPrivileges.hasUserSelectedRightsOnlyForCompanies();
      const viewData = {
        companyId: this.view.currentCompany._id,
        availableServices: this.dependencies.getAvailableServices(),
        showOnlyCompanies: showOnlyCompanies,
        userHasRightsForNetworkTargets: this.view.dom.$roleAndPrivileges.hasUserSelectedRightsForNetworkTargets(),
      } as TargetsViewData;

      if (this.view.preselectedTargets) {
        viewData.preSelectedTargets = this.view.preselectedTargets;
      }

      // start select targets component if doesn't exist
      if (!this.view.dom.$targetsSelection.started) {
        await renderSelectTargetsComponent(this.view.dom.$targetsSelection, viewData, this.dependencies);
        this.setFormValidation();
        return;
      }

      // refresh the component if options has been changed
      if (
        this.view.dom.$targetsSelection.getShowOnlyCompaniesFlag() !== showOnlyCompanies ||
        this.view.dom.$targetsSelection.getCurrentCompanyId() !== this.view.currentCompany._id
      ) {
        delete viewData.preSelectedTargets;
        await renderSelectTargetsComponent(this.view.dom.$targetsSelection, viewData, this.dependencies);
        this.setFormValidation();
      }
    }
  }

  private onAuthenticationMethodChange(event: WebComponentEvent): void {
    // update accountSource when user is changing the authentication method
    if (!this.dependencies.isCloud()) {
      return;
    }

    if (!event.detail.newValue) {
      return;
    }
    this.view.accountSource = event.detail.newValue.value;

    // hide password fields if user select SSO authentication
    if (this.dependencies.isCloud()) {
      this.view.dom.$details.togglePasswordFields(this.view.accountSource !== AccountSource.SSO_SAML);
    }
  }

  public async getDataForView(): Promise<any> {
    const viewData = this.component.getViewData() as EditedAccount;
    this.originalAccountData = viewData;

    if (!this.userFormSelectedCompanyDataProvider) {
      this.userFormSelectedCompanyDataProvider = new UserFormSelectedCompanyDataProvider();
    }
    viewData.companyInfo = await this.userFormSelectedCompanyDataProvider.getUserFormSelectedCompanyData(
      viewData.company.id
    );

    return viewData;
  }

  private setFormValidation(): void {
    this.formSubscription?.unsubscribe();

    if (!isHidden(this.view.dom.$targetsSelection)) {
      this.formSubscription = combineLatest([
        this.view.dom.$details.valid$,
        this.view.dom.$loginSecurity.valid$,
        this.view.dom.$roleAndPrivileges.valid$,
        this.view.dom.$targetsSelection.valid$,
      ]).subscribe({
        next: ([detailsValid, loginSecurityValid, roleAndPrivilegesValid, targetsValid]) => {
          this.isFormValid = detailsValid && loginSecurityValid && roleAndPrivilegesValid && targetsValid;
          this.view.dom.$updateButton.disabled = !this.isFormValid;
        },
      });
      return;
    }

    this.formSubscription = combineLatest([
      this.view.dom.$details.valid$,
      this.view.dom.$loginSecurity.valid$,
      this.view.dom.$roleAndPrivileges.valid$,
    ]).subscribe({
      next: ([detailsValid, loginSecurityValid, roleAndPrivilegesValid]) => {
        this.isFormValid = detailsValid && loginSecurityValid && roleAndPrivilegesValid;
        this.view.dom.$updateButton.disabled = !this.isFormValid;
      },
    });
  }
}
